import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { noticesApi } from '../services/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  Building2,
  Bell,
  FileText,
  Wrench,
  Calendar,
  CreditCard,
  Users,
  Home,
  Settings,
  LogOut,
  Plus,
  ChevronLeft,
  Edit,
  Trash2,
  Pin,
  AlertCircle,
  Menu,
  X,
  Search,
} from 'lucide-react';

const sidebarItems = [
  { icon: Building2, label: 'Dashboard', path: '/admin' },
  { icon: Bell, label: 'Notices', path: '/admin/notices' },
  { icon: FileText, label: 'Documents', path: '/admin/documents' },
  { icon: Wrench, label: 'Maintenance', path: '/admin/maintenance' },
  { icon: Calendar, label: 'Events', path: '/admin/events' },
  { icon: CreditCard, label: 'Payments', path: '/admin/payments' },
  { icon: Users, label: 'Residents', path: '/admin/residents' },
  { icon: Settings, label: 'Settings', path: '/admin/settings' },
];

const categoryColors = {
  general: 'bg-slate-100 text-slate-700',
  maintenance: 'bg-orange-100 text-orange-700',
  event: 'bg-purple-100 text-purple-700',
  urgent: 'bg-red-100 text-red-700',
  security: 'bg-amber-100 text-amber-700',
  other: 'bg-sky-100 text-sky-700',
};

const NOTICE_CATEGORIES = [
  { value: 'general', label: 'General' },
  { value: 'maintenance', label: 'Maintenance' },
  { value: 'event', label: 'Event' },
  { value: 'urgent', label: 'Urgent' },
  { value: 'security', label: 'Security' },
  { value: 'other', label: 'Other' },
];

function NoticeForm({ formData, setFormData, onSubmit, submitLabel }) {
  const update = (k) => (e) => setFormData({ ...formData, [k]: e.target.value });
  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="notice-title">Title *</Label>
        <Input
          id="notice-title"
          value={formData.title}
          onChange={update('title')}
          placeholder="Enter notice title"
          data-testid="notice-title-input"
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="notice-description">Description *</Label>
        <Textarea
          id="notice-description"
          value={formData.description}
          onChange={update('description')}
          placeholder="Enter notice description"
          rows={5}
          data-testid="notice-description-input"
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="notice-category">Category</Label>
        <Select
          value={formData.category}
          onValueChange={(value) => setFormData({ ...formData, category: value })}
        >
          <SelectTrigger id="notice-category" data-testid="notice-category-select">
            <SelectValue placeholder="Select category" />
          </SelectTrigger>
          <SelectContent>
            {NOTICE_CATEGORIES.map((c) => (
              <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label>Audience</Label>
        <p className="text-xs text-slate-500 mb-2">Who should see this notice? Pick one or more groups.</p>
        <div className="grid grid-cols-3 gap-2">
          {[
            { v: 'residents', label: 'Residents' },
            { v: 'trustees', label: 'Trustees' },
            { v: 'management', label: 'Management' },
          ].map((opt) => {
            const checked = (formData.audiences || ['residents']).includes(opt.v);
            return (
              <label
                key={opt.v}
                className={`flex items-center gap-2 px-3 py-2 rounded-md border cursor-pointer text-sm ${checked ? 'border-indigo-400 bg-indigo-50' : 'border-slate-200 hover:border-slate-300'}`}
                data-testid={`audience-${opt.v}`}
              >
                <input
                  type="checkbox"
                  className="rounded border-slate-300"
                  checked={checked}
                  onChange={(e) => {
                    const cur = new Set(formData.audiences || ['residents']);
                    if (e.target.checked) cur.add(opt.v); else cur.delete(opt.v);
                    if (cur.size === 0) cur.add('residents');
                    setFormData({ ...formData, audiences: Array.from(cur) });
                  }}
                />
                {opt.label}
              </label>
            );
          })}
        </div>
      </div>
      <div className="flex items-center gap-2">
        <input
          type="checkbox"
          id="notice-pinned"
          checked={formData.is_pinned}
          onChange={(e) => setFormData({ ...formData, is_pinned: e.target.checked })}
          className="rounded border-slate-300"
          data-testid="notice-pinned-checkbox"
        />
        <Label htmlFor="notice-pinned" className="cursor-pointer">Pin this notice</Label>
      </div>
      <DialogFooter>
        <Button onClick={onSubmit} data-testid="notice-submit-btn">
          {submitLabel}
        </Button>
      </DialogFooter>
    </div>
  );
}

export default function AdminNotices() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [notices, setNotices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [editingNotice, setEditingNotice] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: 'general',
    audiences: ['residents'],
    is_pinned: false,
  });

  const hoaId = searchParams.get('building') || user?.hoa_id || null;

  useEffect(() => {
    if (hoaId) {
      fetchNotices();
    } else {
      setLoading(false);
    }
  }, [hoaId]);

  const fetchNotices = async () => {
    try {
      setLoading(true);
      const data = await noticesApi.list(hoaId);
      setNotices(data);
    } catch (error) {
      console.error('Error fetching notices:', error);
      toast.error(error?.message || 'Failed to load notices');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!formData.title || !formData.description) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      await noticesApi.create({
        ...formData,
        hoa_id: hoaId,
      });
      toast.success('Notice created successfully');
      setIsCreateOpen(false);
      setFormData({ title: '', description: '', category: 'general', audiences: ['residents'], is_pinned: false });
      fetchNotices();
    } catch (error) {
      console.error('Error creating notice:', error);
      toast.error(error?.message || 'Failed to create notice');
    }
  };

  const handleEdit = async () => {
    if (!formData.title || !formData.description) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      await noticesApi.update(editingNotice.notice_id, formData);
      toast.success('Notice updated successfully');
      setIsEditOpen(false);
      setEditingNotice(null);
      setFormData({ title: '', description: '', category: 'general', audiences: ['residents'], is_pinned: false });
      fetchNotices();
    } catch (error) {
      console.error('Error updating notice:', error);
      toast.error(error?.message || 'Failed to update notice');
    }
  };

  const handleDelete = async (noticeId) => {
    if (!confirm('Are you sure you want to delete this notice?')) return;

    try {
      await noticesApi.delete(noticeId);
      toast.success('Notice deleted successfully');
      fetchNotices();
    } catch (error) {
      console.error('Error deleting notice:', error);
      toast.error(error?.message || 'Failed to delete notice');
    }
  };

  const openEditDialog = (notice) => {
    setEditingNotice(notice);
    setFormData({
      title: notice.title,
      description: notice.description,
      category: notice.category,
      audiences: notice.audiences && notice.audiences.length ? notice.audiences : ['residents'],
      is_pinned: notice.is_pinned,
    });
    setIsEditOpen(true);
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const filteredNotices = notices.filter(notice =>
    (notice.title || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
    (notice.description || '').toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {/* Sidebar */}
      <aside className={`fixed inset-y-0 left-0 z-50 w-64 bg-slate-900 transform transition-transform duration-300 lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="flex flex-col h-full">
          <div className="flex items-center justify-between p-4 border-b border-slate-800">
            <Link to="/admin" className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-sky-500 flex items-center justify-center">
                <Building2 className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="font-bold text-white">BodyCorp365</p>
                <p className="text-xs text-slate-400">Admin Portal</p>
              </div>
            </Link>
            <Button variant="ghost" size="icon" className="lg:hidden text-slate-400" onClick={() => setSidebarOpen(false)}>
              <X className="w-5 h-5" />
            </Button>
          </div>

          <nav className="flex-1 overflow-y-auto py-4 px-3">
            <ul className="space-y-1">
              {sidebarItems.map((item) => (
                <li key={item.path}>
                  <Link
                    to={item.path}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${
                      item.path === '/admin/notices'
                        ? 'bg-sky-500 text-white'
                        : 'text-slate-300 hover:text-white hover:bg-slate-800'
                    }`}
                  >
                    <item.icon className="w-5 h-5" />
                    <span className="font-medium">{item.label}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </nav>

          <div className="p-4 border-t border-slate-800">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-sky-600 flex items-center justify-center text-white font-medium">
                {user?.name?.charAt(0) || 'A'}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-white truncate">{user?.name || 'Admin'}</p>
                <p className="text-xs text-slate-400 truncate">{user?.role}</p>
              </div>
              <Button variant="ghost" size="icon" className="text-slate-400 hover:text-white" onClick={handleLogout}>
                <LogOut className="w-5 h-5" />
              </Button>
            </div>
          </div>
        </div>
      </aside>

      {/* Mobile Header */}
      <header className="fixed top-0 left-0 right-0 z-40 lg:hidden bg-white border-b border-slate-200">
        <div className="flex items-center justify-between px-4 py-3">
          <Button variant="ghost" size="icon" onClick={() => setSidebarOpen(true)}>
            <Menu className="w-6 h-6" />
          </Button>
          <span className="font-bold text-slate-900">Notices</span>
          <div className="w-10" />
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 lg:ml-64 pt-16 lg:pt-0">
        <div className="p-4 lg:p-8 max-w-6xl mx-auto">
          {/* Page Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
            <div>
              <h1 className="text-2xl lg:text-3xl font-bold text-slate-900">Notices</h1>
              <p className="text-slate-500 mt-1">Manage community announcements</p>
            </div>
            <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
              <DialogTrigger asChild>
                <Button className="bg-sky-500 hover:bg-sky-600" data-testid="create-notice-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Create Notice
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Create New Notice</DialogTitle>
                  <DialogDescription>
                    Create a new announcement for your community
                  </DialogDescription>
                </DialogHeader>
                <NoticeForm formData={formData} setFormData={setFormData} onSubmit={handleCreate} submitLabel="Create Notice" />
              </DialogContent>
            </Dialog>
          </div>

          {/* Search */}
          <div className="relative mb-6">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <Input
              placeholder="Search notices..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
              data-testid="search-notices-input"
            />
          </div>

          {/* Notices List */}
          {!hoaId ? (
            <Card data-testid="no-building-selected">
              <CardContent className="p-12 text-center">
                <Building2 className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-slate-900 mb-2">Select a building</h3>
                <p className="text-slate-500 mb-4">Choose a building from the Global Building Directory to view and manage its notices.</p>
                <Button onClick={() => navigate('/platform/buildings')} className="bg-sky-500 hover:bg-sky-600">
                  Go to Building Directory
                </Button>
              </CardContent>
            </Card>
          ) : loading ? (
            <div className="space-y-4">
              {[1, 2, 3].map((i) => (
                <Card key={i} className="animate-pulse">
                  <CardContent className="p-6">
                    <div className="h-6 bg-slate-200 rounded w-1/3 mb-2" />
                    <div className="h-4 bg-slate-200 rounded w-full mb-2" />
                    <div className="h-4 bg-slate-200 rounded w-2/3" />
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : filteredNotices.length === 0 ? (
            <Card>
              <CardContent className="p-12 text-center">
                <Bell className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-slate-900 mb-2">No notices yet</h3>
                <p className="text-slate-500 mb-4">Create your first notice to keep residents informed</p>
                <Button onClick={() => setIsCreateOpen(true)} className="bg-sky-500 hover:bg-sky-600">
                  <Plus className="w-4 h-4 mr-2" />
                  Create Notice
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {filteredNotices.map((notice) => (
                <Card key={notice.notice_id} className="hover:shadow-md transition-shadow" data-testid={`notice-${notice.notice_id}`}>
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          {notice.is_pinned && (
                            <Pin className="w-4 h-4 text-sky-500" />
                          )}
                          <h3 className="font-semibold text-slate-900">{notice.title}</h3>
                          <Badge className={categoryColors[notice.category] || categoryColors.general}>
                            {notice.category}
                          </Badge>
                        </div>
                        <p className="text-slate-600 text-sm mb-3">{notice.description}</p>
                        <p className="text-xs text-slate-400">
                          Posted {new Date(notice.created_at).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => openEditDialog(notice)}
                          data-testid={`edit-notice-${notice.notice_id}`}
                        >
                          <Edit className="w-4 h-4 text-slate-500" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDelete(notice.notice_id)}
                          data-testid={`delete-notice-${notice.notice_id}`}
                        >
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </main>

      {/* Edit Dialog */}
      <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Notice</DialogTitle>
            <DialogDescription>
              Update this announcement
            </DialogDescription>
          </DialogHeader>
          <NoticeForm formData={formData} setFormData={setFormData} onSubmit={handleEdit} submitLabel="Save Changes" />
        </DialogContent>
      </Dialog>

      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  );
}
